import { LockIcon } from 'lucide-react';
import { useState } from 'react';

import {
  OrderAction,
  OrderLineManagedAction,
  OrderManagedAction,
  OrderStatus,
  type OrderDTO,
} from '@invenco/sales-interface/interfaces';

import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/core/alert';
import { Button } from '@/components/core/button';
import { EnumMap } from '@/shared/types';

import { orderActionToManagedAction } from './order-action-details';

const managedActionLabel: EnumMap<OrderManagedAction> = {
  RELEASE: 'Releasing the order',
  CANCEL: 'Cancelling the order',
  CLOSE: 'Closing the order',
};

type Props = {
  order: OrderDTO | undefined;
};

// NOTE: Shopify is hardcoded for now, but will likely be made dynamic in the future
export function ManagedAlert({ order }: Props) {
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const lines = order?.orderLines ?? [];
  const isManaged =
    !!order?.managedActions?.length || lines.some((line) => line.managedActions?.length);

  if (!order || !isManaged) return null;

  // if we have managed lines and can edit or could revert to draft in order to edit them, show this in the alert detail
  const hasEditableManagedLines =
    (order.status === OrderStatus.DRAFT ||
      order.availableActions?.includes(OrderAction.REVERT_TO_DRAFT)) &&
    lines.some((line) => line.managedActions?.includes(OrderLineManagedAction.EDIT));

  // only show detail about actions that we could normally perform, but are managed
  const availableManagedActions = (order.availableActions ?? [])
    .map((action) => orderActionToManagedAction[action])
    .filter(
      (managedAction): managedAction is OrderManagedAction =>
        !!managedAction && !!order.managedActions?.includes(managedAction),
    );
  const hasMoreDetail = hasEditableManagedLines || availableManagedActions.length > 0;

  return (
    <div className="px-(--page-inset) pt-(--page-inset)">
      <Alert variant="info" icon={<LockIcon />}>
        <AlertTitle>This order is managed by Shopify</AlertTitle>
        <AlertDescription>
          <p>
            Some changes to this order must be made in the Shopify platform.
            {hasMoreDetail && (
              <>
                {' '}
                <Button variant="link" size="inline" onClick={() => setIsDetailOpen(!isDetailOpen)}>
                  {isDetailOpen ? 'Less' : 'More'} detail.
                </Button>
              </>
            )}
          </p>
          {isDetailOpen && hasMoreDetail && (
            <>
              <p>The following actions are unavailable from within Lynk:</p>
              <ul className="list-disc pl-5">
                {availableManagedActions.map((action) => (
                  <li key={action}>{managedActionLabel[action]}</li>
                ))}
                {hasEditableManagedLines && (
                  <li>
                    Editing items marked by a lock{' '}
                    <LockIcon className="inline size-3.5 align-text-bottom" aria-hidden />
                  </li>
                )}
              </ul>
            </>
          )}
        </AlertDescription>
        {order.externalUrl && (
          <AlertAction>
            <Button variant="outline" asChild>
              <a href={order.externalUrl} target="_blank" rel="noreferrer">
                Manage in Shopify
              </a>
            </Button>
          </AlertAction>
        )}
      </Alert>
    </div>
  );
}
